"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import PinGate from "../components/PinGate";

function ControlDashboard() {
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const camUrl = "/control/api/camera";

  // Servo UI values
  const [s1, setS1] = useState(90); // Base
  const [s3, setS3] = useState(90); // Arm 2
  const [s4, setS4] = useState(90); // Barrel roll

  // Hold-to-move intervals
  const moveInterval1 = useRef<ReturnType<typeof setInterval> | null>(null);
  const moveInterval3 = useRef<ReturnType<typeof setInterval> | null>(null);
  const moveInterval4 = useRef<ReturnType<typeof setInterval> | null>(null);

  const STEP = 1; // degrees per tick
  const INTERVAL = 50; // ms per tick

  const controlBtn =
    "flex-1 rounded-md bg-slate-700 py-2 text-sm hover:bg-slate-600 select-none outline-none focus:outline-none active:outline-none";

  function clampAngle(a: number) {
    return Math.min(180, Math.max(0, a));
  }

  async function sendMoves(moves: { id: number; angle: number }[]) {
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch("/control/api/servo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ moves }),
      });

      const data = await res.json();
      if (data.status !== "ok") {
        setStatus(`Error: ${data.message ?? "Unknown error"}`);
      }
    } catch {
      setStatus("Network error");
    } finally {
      setLoading(false);
    }
  }

  function updateServoLocal(id: number, angle: number) {
    const a = clampAngle(angle);
    if (id === 1) setS1(a);
    if (id === 3) setS3(a);
    if (id === 4) setS4(a);
    return a;
  }

  function updateServo(id: number, angle: number) {
    const a = updateServoLocal(id, angle);
    void sendMoves([{ id, angle: a }]);
  }

  function stopAllMoves() {
    if (moveInterval1.current) {
      clearInterval(moveInterval1.current);
      moveInterval1.current = null;
    }
    if (moveInterval3.current) {
      clearInterval(moveInterval3.current);
      moveInterval3.current = null;
    }
    if (moveInterval4.current) {
      clearInterval(moveInterval4.current);
      moveInterval4.current = null;
    }
  }

const CENTER_MAP: Record<number, number> = {
  1: 125,  // Base
  3: 75,  // Arm 2
  4: 87, // Barrel roll
};

function recenter(ids: number[] = [1, 3, 4]) {
  stopAllMoves();
  ids.forEach((id) => {
    const center = CENTER_MAP[id] ?? 90;
    updateServo(id, center);
  });
}

  // Servo 1 (base): hold left/right
  function startMoveBase(direction: -1 | 1) {
    if (moveInterval1.current) return;
    moveInterval1.current = setInterval(() => {
      setS1((prev) => {
        const next = clampAngle(prev + direction * STEP);
        void sendMoves([{ id: 1, angle: next }]);
        return next;
      });
    }, INTERVAL);
  }
  function stopMoveBase() {
    if (!moveInterval1.current) return;
    clearInterval(moveInterval1.current);
    moveInterval1.current = null;
  }

  // Servo 3 (arm 2): hold up/down
  function startMoveArm2(direction: -1 | 1) {
    if (moveInterval3.current) return;
    moveInterval3.current = setInterval(() => {
      setS3((prev) => {
        const next = clampAngle(prev + direction * STEP);
        void sendMoves([{ id: 3, angle: next }]);
        return next;
      });
    }, INTERVAL);
  }
  function stopMoveArm2() {
    if (!moveInterval3.current) return;
    clearInterval(moveInterval3.current);
    moveInterval3.current = null;
  }

  // Servo 4 (barrel): hold roll left/right
  function startMoveBarrel(direction: -1 | 1) {
    if (moveInterval4.current) return;
    moveInterval4.current = setInterval(() => {
      setS4((prev) => {
        const next = clampAngle(prev + direction * STEP);
        void sendMoves([{ id: 4, angle: next }]);
        return next;
      });
    }, INTERVAL);
  }
  function stopMoveBarrel() {
    if (!moveInterval4.current) return;
    clearInterval(moveInterval4.current);
    moveInterval4.current = null;
  }

  // Cleanup if user navigates away while holding a button
  useEffect(() => {
    return () => stopAllMoves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

const PRESETS: Record<string, Record<number, number>> = {
  Brains:   { 1: 180, 3: 124, 4: 90 },
  BionicHand:   { 1: 80, 3: 75, 4: 180 },
  GyroRobot:  { 1: 44, 3: 82, 4: 90 },
  Power:  { 1: 155, 3: 93, 4: 87 },
};

function moveToPreset(name: keyof typeof PRESETS) {
  stopAllMoves();
  const targets = PRESETS[name];
  Object.entries(targets).forEach(([id, angle]) => {
    updateServo(Number(id), angle);
  });
}


  return (
    <main className="min-h-screen bg-slate-950 text-slate-100 px-6 py-10">
      <div className="mx-auto max-w-4xl">
        <Link
          href="/"
          className="inline-block mb-4 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm hover:bg-slate-800 transition"
        >
          ← Back
        </Link>

        <h1 className="text-2xl font-semibold mb-2">Robot Interface</h1>

        {/* Camera card */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 mt-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">
                CARA (Camera And Robot Arm)
              </h2>
              <p className="text-sm text-slate-400">
                Welcome to my robot control interface!
              </p>
            </div>
            <div className="text-xs text-slate-400">Live View</div>
          </div>

          <div className="w-full max-w-full aspect-video rounded-xl overflow-hidden border border-slate-800 bg-black">
            <img
              src={camUrl}
              alt="Live camera"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Controls card */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Controls</h2>

            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => recenter([1, 3, 4])}
              className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 transition select-none outline-none focus:outline-none active:outline-none"
            >
              ⊙ Re-center
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Base */}
            <div>
              <p className="text-xs text-slate-400 mb-1">Base ({s1}°)</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    startMoveBase(-1);
                  }}
                  onMouseUp={stopMoveBase}
                  onMouseLeave={stopMoveBase}
                  onTouchStart={() => startMoveBase(1)}
                  onTouchEnd={stopMoveBase}
                  className={controlBtn}
                >
                  ◀
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    startMoveBase(1);
                  }}
                  onMouseUp={stopMoveBase}
                  onMouseLeave={stopMoveBase}
                  onTouchStart={() => startMoveBase(-1)}
                  onTouchEnd={stopMoveBase}
                  className={controlBtn}
                >
                  ▶
                </button>
              </div>

            </div>

            {/* Arm 2 */}
            <div>
              <p className="text-xs text-slate-400 mb-1">Arm 2 ({s3}°)</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    startMoveArm2(1);
                  }}
                  onMouseUp={stopMoveArm2}
                  onMouseLeave={stopMoveArm2}
                  onTouchStart={() => startMoveArm2(-1)}
                  onTouchEnd={stopMoveArm2}
                  className={controlBtn}
                >
                  ▲
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    startMoveArm2(-1);
                  }}
                  onMouseUp={stopMoveArm2}
                  onMouseLeave={stopMoveArm2}
                  onTouchStart={() => startMoveArm2(1)}
                  onTouchEnd={stopMoveArm2}
                  className={controlBtn}
                >
                  ▼
                </button>
              </div>

            </div>

            {/* Barrel roll */}
            <div>
              <p className="text-xs text-slate-400 mb-1">
                Barrel Roll ({s4}°)
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    startMoveBarrel(-1);
                  }}
                  onMouseUp={stopMoveBarrel}
                  onMouseLeave={stopMoveBarrel}
                  onTouchStart={() => startMoveBarrel(-1)}
                  onTouchEnd={stopMoveBarrel}
                  className={controlBtn}
                >
                  ⟲
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    startMoveBarrel(1);
                  }}
                  onMouseUp={stopMoveBarrel}
                  onMouseLeave={stopMoveBarrel}
                  onTouchStart={() => startMoveBarrel(1)}
                  onTouchEnd={stopMoveBarrel}
                  className={controlBtn}
                >
                  ⟳
                </button>
              </div>

            </div>
          </div>

          {status && (
            <p className="mt-4 text-sm text-slate-300">
              {loading ? "Sending… " : ""}
              {status}
            </p>
          )}
        </div>
        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
  <p className="text-xs text-slate-400 mb-3">Presets</p>

  <div className="flex flex-wrap gap-2">
    {Object.keys(PRESETS).map((name) => (
      <button
        key={name}
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => moveToPreset(name as keyof typeof PRESETS)}
        className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm hover:bg-slate-800 select-none outline-none focus:outline-none active:outline-none"
      >
        {name}
      </button>
    ))}
  </div>
</div>

        {/* Footer nav */}
        <div className="mt-12 flex items-center justify-between">
          <Link
            href="/"
            className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:bg-slate-900/70"
          >
            ← Back to home
          </Link>

          <Link
            href="/Gyro"
            className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:bg-slate-900/70"
          >
            More projects →
          </Link>
        </div>
      </div>
    </main>
  );
}
